import { useCallback, useEffect, useMemo, useState } from 'react';
import { BrowserRouter, Link, Route, Routes, useNavigate } from 'react-router-dom';
import type { Locale } from '@nabu/shared';
import SignIn from './SignIn';
import Objectives from './Objectives';
import Chat from './Chat';
import NotFound from './NotFound';
import Console from './admin/Console';
import { signOut, whoAmI, type Session } from './api';
import {
  I18nContext,
  LOCALES,
  LOCALE_STORAGE_KEY,
  initialLocale,
  translate,
  useI18n,
  type CopyKey,
} from './i18n';

/**
 * Locale lives above the router, so switching language never navigates.
 *
 * It is also written to `<html lang>`, which is what a screen reader and
 * Google's crawler read — a page whose visible text is Spanish while its lang
 * attribute claims English is worse than either alone.
 */
function I18nProvider({ children }: { children: React.ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(initialLocale);

  useEffect(() => {
    document.documentElement.lang = locale;
  }, [locale]);

  const setLocale = useCallback((next: Locale) => {
    setLocaleState(next);
    try {
      localStorage.setItem(LOCALE_STORAGE_KEY, next);
    } catch {
      // A remembered choice is a convenience, not a requirement. A browser with
      // site data blocked still gets the language it just asked for.
    }
  }, []);

  const value = useMemo(
    () => ({ locale, setLocale, t: (key: CopyKey) => translate(locale, key) }),
    [locale, setLocale],
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

function LocaleSwitch() {
  const { locale, setLocale, t } = useI18n();
  return (
    <div className="locale" role="group" aria-label={t('lang.switch')}>
      {LOCALES.map((code) => (
        <button
          key={code}
          type="button"
          className={code === locale ? 'locale-option is-active' : 'locale-option'}
          aria-pressed={code === locale}
          onClick={() => setLocale(code)}
        >
          {code.toUpperCase()}
        </button>
      ))}
    </div>
  );
}

function Shell({
  children,
  session,
  onChanged,
}: {
  children: React.ReactNode;
  /** `undefined` until the cookie has been asked about. See `Routed`. */
  session: Session | undefined;
  onChanged: () => void;
}) {
  const { t } = useI18n();
  return (
    <>
      <header className="bar">
        <Link to="/" className="mark" aria-label={t('nav.home')}>
          nabu
        </Link>
        <div className="bar-right">
          {session?.signedIn && (
            <Link to="/chat" className="linky">
              {t('session.chat')}
            </Link>
          )}
          {session?.signedIn && (
            <Link to="/objetivos" className="linky">
              {t('session.objectives')}
            </Link>
          )}
          {session?.signedIn && (
            <button
              type="button"
              className="linky"
              onClick={async () => {
                await signOut();
                onChanged();
              }}
            >
              {t('session.signOut')}
            </button>
          )}
          <LocaleSwitch />
        </div>
      </header>
      <main>{children}</main>
      <footer className="bar foot">
        <span>{t('footer.built')}</span>
        <span className="muted">nabu.guru</span>
      </footer>
    </>
  );
}

function Step({ n, title, body }: { n: number; title: string; body: string }) {
  return (
    <li className="step">
      <span className="step-n" aria-hidden="true">
        {n}
      </span>
      <div>
        <h3>{title}</h3>
        <p>{body}</p>
      </div>
    </li>
  );
}

function Home({ session }: { session: Session | undefined }) {
  const { t } = useI18n();
  const navigate = useNavigate();
  return (
    <>
      <section className="hero">
        <h1>{t('home.tagline')}</h1>
        <p className="lede">{t('home.lede')}</p>
      </section>

      <section className="panel">
        <h2>{t('home.how.title')}</h2>
        <ol className="steps">
          <Step n={1} title={t('home.how.1.title')} body={t('home.how.1.body')} />
          <Step n={2} title={t('home.how.2.title')} body={t('home.how.2.body')} />
          <Step n={3} title={t('home.how.3.title')} body={t('home.how.3.body')} />
        </ol>
      </section>

      <section className="panel">
        <h2>{t('home.why.title')}</h2>
        <p>{t('home.why.body')}</p>
      </section>

      <section className="panel cta">
        <h2>{t('home.cta.title')}</h2>
        <p>{t('home.cta.body')}</p>
        {/* Nothing at all until the answer is in. The two buttons go to different
            places, so rendering a guess means the one somebody taps in the first
            half-second is sometimes the wrong one. */}
        {session === undefined ? (
          <p className="muted">{t('session.checking')}</p>
        ) : session.signedIn ? (
          <button type="button" className="button primary" onClick={() => navigate('/objetivos')}>
            {t('session.objectives')}
          </button>
        ) : (
          <button type="button" className="button primary" onClick={() => navigate('/entrar')}>
            {t('home.cta.button')}
          </button>
        )}
      </section>
    </>
  );
}

function Routed() {
  /*
   * `undefined` means "not asked yet", and that is a different thing from signed out.
   *
   * It used to start at `{ signedIn: false }`, which is an assertion rather than an
   * absence — so every reload of a signed-in page painted the sign-in screen, then
   * replaced it when `whoAmI()` answered. A flash of "you are not logged in" at
   * somebody who is is alarming in a way a moment of blankness is not.
   *
   * The page genuinely cannot know without asking: the session cookie is HttpOnly by
   * design, so it is invisible to JavaScript. The fix is not to guess faster, it is to
   * stop guessing.
   */
  const { t } = useI18n();
  const [session, setSession] = useState<Session>();

  const refresh = useCallback(() => {
    void whoAmI().then(setSession);
  }, []);

  // Asked once on load. This is what makes a reload keep you signed in.
  useEffect(refresh, [refresh]);

  return (
    <Shell session={session} onChanged={refresh}>
      <Routes>
        <Route path="/" element={<Home session={session} />} />
        <Route path="/entrar" element={<SignIn onSignedIn={refresh} />} />
        <Route
          path="/objetivos"
          element={
            session === undefined ? (
              // Deliberately quiet, and deliberately not the sign-in screen. This is
              // the branch the bug lived in.
              <section className="panel">
                <p className="muted">{t('session.checking')}</p>
              </section>
            ) : session.signedIn ? (
              <Objectives />
            ) : (
              <SignIn onSignedIn={refresh} />
            )
          }
        />
        {/*
          The conversation (F-100). Guarded exactly as `/objetivos` is, and for the
          reason written there: `undefined` is "not asked yet" and must not paint the
          sign-in screen at somebody who is signed in.
        */}
        <Route
          path="/chat"
          element={
            session === undefined ? (
              <section className="panel">
                <p className="muted">{t('session.checking')}</p>
              </section>
            ) : session.signedIn ? (
              <Chat />
            ) : (
              <SignIn onSignedIn={refresh} />
            )
          }
        />
        {/*
          The console. Mounted for everybody, because whether it exists is not a
          question the browser can answer — `/api/admin/me` decides, and a
          non-operator gets the same `NotFound` as any wrong URL (D-20). Matched as
          `/admin/*` so the console owns its own subpaths without App knowing them.
        */}
        <Route path="/admin/*" element={<Console />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Shell>
  );
}

export default function App() {
  return (
    <I18nProvider>
      <BrowserRouter>
        <Routed />
      </BrowserRouter>
    </I18nProvider>
  );
}
